import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ShieldCheck, FileText, FileSpreadsheet, FileType, Sparkles, PenLine, Send, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { bizDate } from '@/lib/dates';
import { toast } from 'sonner';

// Findings 2+3 — single canonical report view (tab "Informe ejecutivo").
// Polish sprint: consultant metadata card + Professional Interpretation workspace.
export default function ClimaraReportView({ campaignId, onChanged }) {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'en').startsWith('es') ? 'es' : 'en';
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [actionInFlight, setActionInFlight] = useState(false);
  const [interpText, setInterpText] = useState('');
  const [interpBusy, setInterpBusy] = useState(null);
  const [staleWarn, setStaleWarn] = useState(null); // {draft_saved_at, results_generated_at}

  const fetchReport = async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const list = await adminApi.climaraListReports(null);
      const existing = list.find((r) => r.campaign_id === campaignId);
      const full = existing ? await adminApi.climaraGetReport(existing.id) : null;
      setReport(full);
      setInterpText(full?.interpretation_note?.text || '');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, [campaignId]);

  const generate = async () => {
    setActionInFlight(true);
    try {
      const fresh = await adminApi.climaraGenerateReport(campaignId, locale);
      const full = await adminApi.climaraGetReport(fresh.id);
      setReport(full);
      setInterpText(full?.interpretation_note?.text || '');
      toast.success(t('climara.report.generated', { defaultValue: 'Executive report generated' }));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActionInFlight(false);
    }
  };

  const regenerate = async () => {
    setActionInFlight(true);
    try {
      const fresh = await adminApi.climaraGenerateReport(campaignId, locale);
      const full = await adminApi.climaraGetReport(fresh.id);
      setReport(full);
      toast.success(t('climara.report.regenerated', { defaultValue: 'Report regenerated' }));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActionInFlight(false);
    }
  };

  const release = async () => {
    if (!report) return;
    setActionInFlight(true);
    try {
      const released = await adminApi.climaraReleaseReport(report.id);
      setReport((prev) => ({ ...prev, ...released }));
      toast.success(t('climara.report.released', { defaultValue: 'Report released to client' }));
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === 'professional_review_required') {
        toast.error(t('climara.report.reviewRequired', { defaultValue: 'Antes de publicar, marca la revisión profesional del informe como completada.' }));
      } else if (detail === 'interpretation_review_required') {
        toast.error(t('climara.report.interpStale', { defaultValue: 'Los resultados cambiaron desde la interpretación. Revisa y vuelve a publicar la interpretación antes de liberar el informe.' }));
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setActionInFlight(false);
    }
  };

  const markReviewed = async () => {
    if (!report) return;
    setActionInFlight(true);
    try {
      const review = await api.post(`/admin/climara/reports/${report.id}/mark-reviewed`).then((r) => r.data);
      setReport((prev) => ({ ...prev, review }));
      toast.success(t('climara.report.reviewedOk', { defaultValue: 'Revisión profesional registrada' }));
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setActionInFlight(false);
  };

  const downloadExport = async (ext) => {
    if (!report) return;
    try {
      const res = await api.get(
        `/admin/climara/reports/${report.id}/export.${ext}`,
        { responseType: 'blob', params: { locale } },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `climara-${report.id.slice(0, 8)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // --- Item 1: interpretation workspace actions (HM only — this view is HM-only) ---
  const saveInterpretation = async () => {
    setInterpBusy('save');
    try {
      const note = await adminApi.climaraSaveInterpretation(report.id, interpText);
      setReport((prev) => ({ ...prev, interpretation_note: note }));
      toast.success(t('climara.interp.savedDraft', { defaultValue: 'Interpretation draft saved' }));
    } catch (e) { toast.error(formatApiError(e)); }
    setInterpBusy(null);
  };
  const publishInterpretation = async (acknowledgeStale = false) => {
    setInterpBusy('publish');
    try {
      const note = await adminApi.climaraPublishInterpretation(report.id, interpText, acknowledgeStale);
      setReport((prev) => ({ ...prev, interpretation_note: note }));
      setStaleWarn(null);
      toast.success(t('climara.interp.published', { defaultValue: 'Interpretation published — the client will see it with the released report' }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'stale_interpretation_draft') {
        // Staleness guard — results regenerated after the draft was written.
        setStaleWarn(detail);
      } else {
        toast.error(formatApiError(e));
      }
    }
    setInterpBusy(null);
  };
  const aiInterpretation = async () => {
    setInterpBusy('ai');
    try {
      const r = await adminApi.climaraAiInterpretation(report.id, locale);
      setInterpText(r.text || '');
      toast.success(t('climara.interp.aiReady', { defaultValue: 'AI proposal generated — review and adjust it before publishing' }));
    } catch (e) { toast.error(formatApiError(e)); }
    setInterpBusy(null);
  };

  const payload = report?.payload;
  const totals = payload?.totals;
  const scale = payload?.scale || { min: 1, max: 5 };
  const dimensions = payload?.dimensions || [];
  const depts = payload?.department_breakdown || [];
  const themes = payload?.qualitative_themes || [];
  const suppression = payload?.department_suppression || {};
  const note = report?.interpretation_note || null;

  const dimColor = (avg) => {
    if (avg == null) return '#94a3b8';
    const ratio = (avg - scale.min) / Math.max(0.0001, scale.max - scale.min);
    if (ratio >= 0.8) return '#16a34a';
    if (ratio >= 0.6) return '#65a30d';
    if (ratio >= 0.4) return '#ca8a04';
    if (ratio >= 0.2) return '#ea580c';
    return '#dc2626';
  };

  const isReleased = report?.status === 'released';
  const d10 = (iso) => (iso ? bizDate(iso, locale) : '—');
  const review = report?.review || null;
  const reviewCurrent = !!review && review.reviewed_generated_at === report?.generated_at;
  const interpStale = !!note?.stale;

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-muted-foreground" data-testid="climara-report-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
      </div>
    );
  }

  if (!report || !payload) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center space-y-3" data-testid="climara-report-empty">
        <p className="text-sm font-medium">
          {t('climara.report.noReportYet', { defaultValue: 'No executive report has been generated for this campaign yet.' })}
        </p>
        <p className="text-xs text-muted-foreground max-w-xl mx-auto">
          {t('climara.report.noReportHint', { defaultValue: 'You can generate it at any time — closing the campaign is not required. The consultant decides when participation is sufficient.' })}
        </p>
        <Button onClick={generate} disabled={actionInFlight} className="bg-gradient-primary hover:opacity-90" data-testid="climara-report-generate-btn">
          {actionInFlight ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
          {t('climara.report.generateCta', { defaultValue: 'Generate executive report' })}
        </Button>
      </div>
    );
  }

  const SectionHead = ({ n, title, sub }) => (
    <div className="border-b pb-2 mb-4">
      <h3 className="font-display font-bold text-base tracking-tight">
        <span className="text-amber-700 tabular-nums mr-2">{String(n).padStart(2, '0')}</span>{title}
      </h3>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="climara-report-view">
      {/* Action toolbar */}
      <div className="flex flex-wrap items-center gap-2" data-testid="climara-report-toolbar">
        <div className="mr-2">
          <p className="text-sm font-semibold" data-testid="climara-report-campaign-name">
            {payload?.campaign?.name || ''}{payload?.campaign?.period ? ` · ${payload.campaign.period}` : ''}
          </p>
        </div>
        <Badge
          variant={isReleased ? 'default' : 'outline'}
          className="text-xs"
          data-testid="climara-report-status"
        >
          {isReleased
            ? t('climara.report.statusReleased', { defaultValue: 'Released to client' })
            : t('climara.report.statusDraft', { defaultValue: 'Draft — not visible to client' })}
        </Badge>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={regenerate} disabled={actionInFlight} data-testid="climara-report-regenerate">
          {actionInFlight && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
          {t('climara.report.regenerate', { defaultValue: 'Regenerate' })}
        </Button>
        {!isReleased && !reviewCurrent && (
          <Button size="sm" variant="outline" onClick={markReviewed} disabled={actionInFlight}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="climara-report-mark-reviewed">
            <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />
            {t('climara.report.markReviewed', { defaultValue: 'Marcar revisión profesional completada' })}
          </Button>
        )}
        {!isReleased && (
          <Button size="sm" onClick={release} disabled={actionInFlight || !reviewCurrent}
                  title={!reviewCurrent ? t('climara.report.reviewRequired', { defaultValue: 'Antes de publicar, marca la revisión profesional del informe como completada.' }) : undefined}
                  className="bg-gradient-primary hover:opacity-90 disabled:opacity-50" data-testid="climara-report-release">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
            {t('climara.report.release', { defaultValue: 'Release to client' })}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => downloadExport('pdf')} data-testid="climara-report-export-pdf">
          <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
        </Button>
        <Button size="sm" variant="outline" onClick={() => downloadExport('docx')} data-testid="climara-report-export-docx">
          <FileType className="h-3.5 w-3.5 mr-1.5" /> Word
        </Button>
        <Button size="sm" variant="outline" onClick={() => downloadExport('xlsx')} data-testid="climara-report-export-xlsx">
          <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
        </Button>
      </div>

      {/* Item 5 — consultant metadata (HM-only view) */}
      <div className="rounded-lg border bg-muted/30 px-4 py-3 flex flex-wrap gap-x-8 gap-y-2" data-testid="climara-report-meta">
        <MetaItem label={t('climara.meta.generated', { defaultValue: 'Generated on' })} value={d10(report.generated_at)} testid="climara-meta-generated" />
        <MetaItem label={t('climara.meta.wave', { defaultValue: 'Wave' })} value={payload?.campaign?.period || payload?.campaign?.name || '—'} testid="climara-meta-wave" />
        <MetaItem
          label={t('climara.meta.status', { defaultValue: 'Report status' })}
          value={isReleased
            ? `${t('climara.meta.published', { defaultValue: 'Published' })} · ${d10(report.released_at)}`
            : t('climara.meta.draft', { defaultValue: 'Draft' })}
          testid="climara-meta-status"
        />
        <MetaItem
          label={t('climara.meta.review', { defaultValue: 'Revisión profesional' })}
          value={reviewCurrent
            ? `${review.reviewed_by_name || '—'} · ${d10(review.reviewed_at)}`
            : t('climara.meta.reviewPending', { defaultValue: 'Pendiente' })}
          testid="climara-meta-review"
        />
        <MetaItem
          label={t('climara.meta.interp', { defaultValue: 'Interpretation' })}
          value={note?.text
            ? `${note.author_name || '—'} · ${note.status === 'published'
              ? t('climara.meta.interpPublished', { defaultValue: 'published' })
              : t('climara.meta.interpDraft', { defaultValue: 'draft' })}`
            : t('climara.meta.interpNone', { defaultValue: 'not written yet' })}
          testid="climara-meta-interp"
        />
      </div>

      {/* FIX 10 — explicit client-visibility hierarchy: one unambiguous line */}
      <div className={`rounded-lg border px-4 py-2.5 text-xs ${isReleased ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`}
           data-testid="climara-visibility-summary">
        <span className="font-semibold">{t('climara.visibility.label', { defaultValue: 'Visible para el cliente ahora:' })} </span>
        {isReleased
          ? (note?.status === 'published'
            ? t('climara.visibility.reportAndInterp', { defaultValue: 'Informe ejecutivo publicado + interpretación profesional publicada.' })
            : t('climara.visibility.reportOnly', { defaultValue: 'Informe ejecutivo publicado (sin interpretación publicada).' }))
          : t('climara.visibility.nothing', { defaultValue: 'Nada — el informe está en borrador. La interpretación, aunque esté publicada, solo se mostrará al cliente cuando el informe sea publicado.' })}
      </div>

      {/* FIX 12 — data changed after the interpretation was written */}
      {interpStale && (
        <div className="flex items-start gap-2.5 rounded-lg border border-amber-400 bg-amber-50 p-3" data-testid="climara-interp-stale-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs leading-relaxed">
            <p className="font-semibold text-amber-900">
              {t('climara.interp.staleTitle', { defaultValue: 'Los resultados de la campaña cambiaron desde esta interpretación. Revisión requerida.' })}
            </p>
            <p className="text-amber-800 mt-0.5">
              {t('climara.interp.staleBody', { defaultValue: 'Revisa/actualiza el texto y vuelve a publicar la interpretación para reconfirmarla. La versión anterior se conserva en el historial. El informe no puede publicarse hasta reconfirmar.' })}
            </p>
          </div>
        </div>
      )}

      {/* Participation */}
      <section data-testid="climara-report-totals">
        <SectionHead n={1} title={t('climara.report.rate', { defaultValue: 'Completion rate' })} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label={t('climara.report.invited', { defaultValue: 'Invited' })} value={totals?.invited ?? 0} />
          <Kpi label={t('climara.report.responded', { defaultValue: 'Responded' })} value={totals?.responded ?? 0} tone="emerald" />
          <Kpi label={t('climara.report.pending', { defaultValue: 'Pending' })} value={totals?.pending ?? 0} tone="amber" />
          <Kpi label={t('climara.report.rate', { defaultValue: 'Completion rate' })} value={`${totals?.rate_pct ?? 0}%`} tone="sky" />
        </div>
      </section>

      {/* Dimensions — DYNAMIC */}
      <section data-testid="climara-report-dimensions">
        <SectionHead n={2}
          title={t('climara.report.dimensions', { defaultValue: 'Dimensions' })}
          sub={t('climara.report.dimensionsSub', { defaultValue: 'Detected automatically from the questionnaire structure.' })} />
        {dimensions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('climara.report.noDimensions', { defaultValue: 'No dimensions detected.' })}
          </p>
        ) : (
          <div className="space-y-3">
            {dimensions.map((d) => (
              <div key={d.code} data-testid={`climara-report-dim-${d.code}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{d.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">n={d.n}</span>
                    <span className="text-sm font-bold tabular-nums" style={{ color: dimColor(d.avg) }}>
                      {d.avg == null ? '—' : d.avg.toFixed(2)}
                    </span>
                  </span>
                </div>
                <Progress
                  value={d.avg == null ? 0 : Math.max(0, Math.min(100, ((d.avg - scale.min) / (scale.max - scale.min)) * 100))}
                  className="h-1.5"
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Departments */}
      <section data-testid="climara-report-departments">
        <SectionHead n={3}
          title={t('climara.report.departments', { defaultValue: 'Department breakdown' })}
          sub={t('climara.report.deptsSub', {
            defaultValue: 'Departments with fewer than {{n}} responses are merged into "Other Departments" for anonymity.',
            n: suppression.threshold ?? 5,
          })} />
        {depts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('climara.report.noDepts', { defaultValue: 'No department data yet.' })}
          </p>
        ) : (
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/40 text-left">
                  <th className="px-2 py-1.5">{t('climara.report.department', { defaultValue: 'Department' })}</th>
                  <th className="px-2 py-1.5 text-center">n</th>
                  {dimensions.map((d) => (
                    <th key={d.code} className="px-2 py-1.5 text-center whitespace-nowrap">{d.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {depts.map((dr, i) => (
                  <tr key={i} className="border-t" data-testid={`climara-report-dept-${i}`}>
                    <td className="px-2 py-1.5 font-medium">{dr.department_name}</td>
                    <td className="px-2 py-1.5 text-center tabular-nums">{dr.n}</td>
                    {dr.dimensions.map((dim) => (
                      <td key={dim.code} className="px-2 py-1.5 text-center tabular-nums" style={{ color: dimColor(dim.avg) }}>
                        {dim.avg == null ? '—' : dim.avg.toFixed(2)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Qualitative */}
      <section data-testid="climara-report-qualitative">
        <SectionHead n={4} title={t('climara.report.qualitative', { defaultValue: 'Anonymous comments' })} />
        {themes.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            {t('climara.report.noQualitative', { defaultValue: 'No qualitative responses captured.' })}
          </p>
        ) : (
          <div className="space-y-3">
            {themes.map((th, i) => (
              <div key={i} className="border-l-2 border-primary/40 pl-3" data-testid={`climara-report-theme-${i}`}>
                <div className="text-sm font-semibold flex items-center gap-2">{th.prompt}
                  <Badge variant="outline" className="text-xs">{th.count}</Badge>
                </div>
                <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground italic">
                  {(th.responses || []).slice(0, 6).map((r, j) => (
                    <li key={j}>&ldquo;{r}&rdquo;</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Item 1 — Professional Interpretation workspace (HM only) */}
      <section
        className="rounded-xl border border-amber-700/25 bg-amber-50/30 px-5 py-5"
        data-testid="climara-report-interpretation"
      >
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <PenLine className="h-4 w-4 text-amber-700" />
          <h3 className="font-display font-bold text-base tracking-tight">
            {t('climara.interp.title', { defaultValue: 'Professional Interpretation' })}
          </h3>
          <Badge
            className={note?.status === 'published'
              ? 'bg-emerald-100 text-emerald-700'
              : note?.text ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}
            data-testid="climara-interp-status"
          >
            {note?.status === 'published'
              ? (isReleased
                ? t('climara.interp.statusPublished', { defaultValue: 'Published — visible to client' })
                : t('climara.interp.statusPublishedPending', { defaultValue: 'Publicada — se mostrará al cliente cuando el informe sea publicado' }))
              : note?.text
                ? t('climara.interp.statusDraft', { defaultValue: 'Draft — not visible to client' })
                : t('climara.interp.statusNone', { defaultValue: 'Not written yet' })}
          </Badge>
          {interpStale && (
            <Badge className="bg-amber-100 text-amber-800 gap-1" data-testid="climara-interp-stale-badge">
              <AlertTriangle className="h-3 w-3" />
              {t('climara.interp.staleBadge', { defaultValue: 'Revisión requerida — datos actualizados' })}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          {t('climara.interp.sub', { defaultValue: 'The consulting team\u2019s signed reading of this wave. Generate an AI proposal, edit it freely, save drafts and publish the final version — the client only ever sees the published text.' })}
        </p>
        <Textarea
          value={interpText}
          onChange={(e) => setInterpText(e.target.value)}
          rows={8}
          placeholder={t('climara.interp.placeholder', { defaultValue: 'Write the professional interpretation here, or generate an AI proposal as a starting point…' })}
          className="bg-white text-sm leading-relaxed"
          data-testid="climara-interp-textarea"
        />
        {staleWarn && (
          <div className="mt-3 rounded-lg border-l-4 border-warning bg-warning/10 px-4 py-3" data-testid="climara-interp-stale-warning">
            <p className="text-sm font-semibold flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 text-warning" />
              {t('climara.interp.staleGuardTitle', { defaultValue: 'Results changed after this draft was written' })}
            </p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {t('climara.interp.staleGuardBody', { defaultValue: 'The results have changed since this interpretation was drafted. Review the content before publishing.' })}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1.5 tabular-nums" data-testid="climara-interp-stale-dates">
              {t('climara.interp.staleDraftAt', { defaultValue: 'Draft written' })}: {d10(staleWarn.draft_saved_at)} · {t('climara.interp.staleResultsAt', { defaultValue: 'Current results generated' })}: {d10(staleWarn.results_generated_at)}
            </p>
            <div className="flex gap-2 mt-2.5">
              <Button size="sm" variant="outline" onClick={() => setStaleWarn(null)} data-testid="climara-interp-stale-review-btn">
                {t('climara.interp.staleReview', { defaultValue: 'Go back and review' })}
              </Button>
              <Button size="sm" variant="destructive" onClick={() => publishInterpretation(true)} disabled={!!interpBusy}
                      data-testid="climara-interp-stale-ack-btn">
                {interpBusy === 'publish' && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                {t('climara.interp.staleAck', { defaultValue: 'I reviewed it — publish anyway' })}
              </Button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={aiInterpretation} disabled={!!interpBusy} data-testid="climara-interp-ai-btn">
            {interpBusy === 'ai' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            {t('climara.interp.aiBtn', { defaultValue: 'Generate AI proposal' })}
          </Button>
          <Button size="sm" variant="outline" onClick={saveInterpretation} disabled={!!interpBusy} data-testid="climara-interp-save-btn">
            {interpBusy === 'save' && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            {t('climara.interp.saveBtn', { defaultValue: 'Save draft' })}
          </Button>
          <Button size="sm" onClick={() => publishInterpretation(false)}
                  disabled={!!interpBusy || !interpText.trim()}
                  className="bg-gradient-primary hover:opacity-90" data-testid="climara-interp-publish-btn">
            {interpBusy === 'publish' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
            {t('climara.interp.publishBtn', { defaultValue: 'Publish interpretation' })}
          </Button>
          {note?.updated_at && (
            <span className="text-xs text-muted-foreground ml-auto" data-testid="climara-interp-author">
              {note.author_name || ''} · {d10(note.updated_at)}
            </span>
          )}
        </div>
      </section>
    </div>
  );
}

function MetaItem({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className="text-xs font-medium mt-0.5">{value}</p>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const tones = {
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    sky: 'text-sky-700',
  };
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className={`text-2xl font-bold tabular-nums mt-0.5 ${tones[tone] || 'text-foreground'}`}>{value}</p>
    </div>
  );
}
